import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import Modal from './modal';

/**
 * Reusable confirmation dialog built on top of the canonical `<Modal>`.
 *
 * Two ways to use it:
 *
 *   1) Controlled (you own the open/close state):
 *
 *      <ConfirmDialog
 *        isOpen={ showConfirm }
 *        title="Cancel subscription?"
 *        message="This cannot be undone."
 *        confirmLabel="Cancel subscription"
 *        cancelLabel="Keep it"
 *        variant="danger"
 *        onConfirm={ async () => { await doIt(); setShowConfirm(false); } }
 *        onClose={ () => setShowConfirm(false) }
 *      />
 *
 *   2) Async helper (uses the `useConfirm` hook below). See its comments.
 *
 * Notes:
 *   - The confirm button is `disabled` and shows "..." while `onConfirm` is
 *     in-flight, so async handlers are safe.
 *   - `confirmDataTa` is exposed so E2E tests can target the confirm button
 *     specifically, since both buttons live inside the same modal.
 */
const ConfirmDialog = ({
	isOpen,
	onConfirm,
	onClose,
	title = 'Are you sure?',
	message,
	children,
	confirmLabel = 'Confirm',
	cancelLabel = 'Cancel',
	variant = 'default',         // 'default' | 'danger'
	size = 'sm',
	dataTa = 'confirm-dialog',
	confirmDataTa = 'confirm-dialog-confirm',
	cancelDataTa = 'confirm-dialog-cancel',
	closeOnOverlayClick = true,
	closeOnEsc = true,
}) => {
	const [ pending, setPending ] = useState(false);

	const handleConfirm = useCallback(async () => {
		if (pending) return;
		try {
			setPending(true);
			await onConfirm();
		} finally {
			setPending(false);
		}
	}, [ onConfirm, pending ]);

	const handleCancel = useCallback(() => {
		if (pending) return;
		onClose();
	}, [ onClose, pending ]);

	const confirmButtonClass = variant === 'danger'
		? 'button alert margin-0'
		: 'button primary margin-0';

	const footer = (
		<div className="grid-x align-right" style={ { gap: '0.5rem' } }>
			<button
				type="button"
				className="button hollow margin-0"
				onClick={ handleCancel }
				disabled={ pending }
				data-ta={ cancelDataTa }
			>
				{ cancelLabel }
			</button>
			<button
				type="button"
				className={ confirmButtonClass }
				onClick={ handleConfirm }
				disabled={ pending }
				data-ta={ confirmDataTa }
			>
				{ pending ? '…' : confirmLabel }
			</button>
		</div>
	);

	return (
		<Modal
			isOpen={ isOpen }
			onClose={ handleCancel }
			size={ size }
			title={ title }
			footer={ footer }
			dataTa={ dataTa }
			closeOnOverlayClick={ closeOnOverlayClick && !pending }
			closeOnEsc={ closeOnEsc && !pending }
		>
			{ children || (
				<p className="margin-bottom-0">{ message }</p>
			) }
		</Modal>
	);
};

ConfirmDialog.propTypes = {
	isOpen: PropTypes.bool.isRequired,
	onConfirm: PropTypes.func.isRequired,
	onClose: PropTypes.func.isRequired,
	title: PropTypes.node,
	message: PropTypes.node,
	children: PropTypes.node,
	confirmLabel: PropTypes.string,
	cancelLabel: PropTypes.string,
	variant: PropTypes.oneOf([ 'default', 'danger' ]),
	size: PropTypes.oneOf([ 'sm', 'md', 'lg', 'xl', 'fullscreen' ]),
	dataTa: PropTypes.string,
	confirmDataTa: PropTypes.string,
	cancelDataTa: PropTypes.string,
	closeOnOverlayClick: PropTypes.bool,
	closeOnEsc: PropTypes.bool,
};

export default ConfirmDialog;
